import React, { useEffect, useRef, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { SearchInput } from "@appsmith/ads";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 260px;
  max-width: 90vw;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 1px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 9999;
  animation: ${fadeIn} 120ms ease-out forwards;
  color: #222;
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.94rem;
  padding: 4px 8px;
  border-radius: 1px;
  background: transparent;
  color: inherit;
  &::placeholder {
    color: #777;
  }
  &:focus {
    box-shadow: 0 0 0 2px #0066ff;
  }
`;

const SmallBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, transform 0.12s;
  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
  &:active {
    background: rgba(0, 0, 0, 0.12);
    transform: scale(0.94);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const ArrowUp = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);
const ArrowDown = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const Cross = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

type SearchOverlayProps = {
  onSearch: (query: string) => void;
  onClose: () => void;
  onReset?: () => void;
  enableNav?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  totalMatches?: number;
  currentMatch?: number;
};

export const SearchOverlay: React.FC<SearchOverlayProps> = ({
  onSearch,
  onClose,
  onReset,
  enableNav = true,
  onPrev,
  onNext,
  totalMatches,
  currentMatch,
}) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);

  const safeClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setQuery("");
    onReset?.();
    onClose();
  }, [onClose, onReset, onSearch]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = useCallback(() => {
    const trimmed = query.trim();
    if (trimmed) onSearch(trimmed);
  }, [query, onSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        submit();
        break;
      case "Escape":
        e.preventDefault();
        safeClose();
        break;
      case "ArrowUp":
        if (onPrev) {
          e.preventDefault();
          onPrev();
        }
        break;
      case "ArrowDown":
        if (onNext) {
          e.preventDefault();
          onNext();
        }
        break;
    }
  };


  useEffect(() => {
    const handleDocClick = (ev: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(ev.target as Node)) {
        safeClose();
        setQuery("")
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
    };
  }, [safeClose]);

  const renderCounter = () => {
    if (totalMatches == null || currentMatch == null) return null;
    const cur = Math.max(0, Math.min(currentMatch, totalMatches));
    return <Counter>{cur} / {totalMatches}</Counter>;
  };

  return (
    <Wrapper ref={wrapperRef}>
      <SearchInput
        ref={inputRef}
        placeholder="Search…"
        value={query}
        onChange={e => setQuery(e)}
        onKeyDown={handleKeyDown}
      />
      {renderCounter()}
      {enableNav && (
        <>
          <SmallBtn
            onClick={onPrev}
            aria-label="Previous match"
            disabled={!onPrev || (totalMatches || 0 ) <= 1}
          >
            <ArrowUp />
          </SmallBtn>
          <SmallBtn
            onClick={onNext}
            aria-label="Next match"
            disabled={!onNext || (totalMatches || 0 ) <= 1}
          >
            <ArrowDown />
          </SmallBtn>
        </>
      )}
      <SmallBtn onClick={safeClose} aria-label="Close search">
        <Cross />
      </SmallBtn>
    </Wrapper>
  );
};

const Counter = styled.span`
  font-size: 0.85rem;
  color: #555;
  margin-left: 2px;
  white-space: nowrap;
`;